import React, { useMemo } from 'react';
import {
  Dimensions,
} from 'react-native';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  ButtonText,
  useMedia,
  Center,
  Spinner,
  Icon,
  useToken,
  Pressable,
  ScrollView,
} from '@gluestack-ui/themed';
import {
  Calendar,
  GraduationCap,
  Layout,
  Play,
  CheckCircle2,
  Circle,
  Bell,
  Lock,
  Trophy,
  Clock,
} from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';
import { addWeeks } from 'date-fns';

import { useGetUserBookings } from '@/common/api/endpoints/appointments/appointments';
import { useModuleAndTaskSelectionState } from '@/common/context/AppStateStore';
import { getColorForModule } from '@/common/utils/getColorForModule';
import { User } from '@/common/api/models/user';
import { Module } from '@/student/course/types';
import { useStudyPlan } from '@/student/course/hooks/useStudyPlan';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import { ActivityCard } from './ActivityCard';
import { StudyPacing } from './StudyPacing';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

interface Semester {
  id: string;
  title: string;
  startMonth: number;
  endMonth: number;
  modules: (Module & { expectedFinishDate: Date })[];
  dateRange?: string;
  year?: number;
}

interface CourseGroup {
  id: string;
  title: string;
  semesters: Semester[];
}

interface ProgramArchitectProps {
  modules: Module[];
  onSelectModule: (index: number) => void;
  onOpenModule: (params: { moduleId: string; courseId?: string }) => void;
  user: User;
  pageHeight: number;
}

const getSemesterInfo = (date: Date) => {
  const month = date.getMonth();
  const year = date.getFullYear();

  if (month >= 8 || month <= 2) {
    const startYear = month >= 8 ? year : year - 1;
    const endYear = startYear + 1;
    return {
      id: `WS-${startYear}-${endYear}`,
      title: `Winter Term ${startYear}/${endYear % 100}`,
      startMonth: 8,
      endMonth: 2,
    };
  }
  return {
    id: `SS-${year}`,
    title: `Summer Term ${year}`,
    startMonth: 3,
    endMonth: 7,
  };
};

/**
 * ProgramArchitect is a comprehensive dashboard component that visualizes
 * the student's entire study program, upcoming activities, and pacing.
 */
export const ProgramArchitect: React.FC<ProgramArchitectProps> = ({
  modules,
  onSelectModule,
  onOpenModule,
  user,
  pageHeight,
}) => {
  const { t } = useTranslation();
  const media = useMedia();
  // @ts-expect-error - primaryColor is a dynamic token
  const primaryColorHex = useToken('colors', 'primaryColor') as string;
  const { selectedModule } = useModuleAndTaskSelectionState();
  const displayHeight = pageHeight || SCREEN_HEIGHT || 800;

  const legacyMode = useTenantSettingsStore((state) => state.getLegacyCourseCompatibilityMode());

  // Use the new StudyPlan hook
  const {
    studyPlan,
    updatePace,
    projectedGraduationDate,
    progressPercentage,
    modulesCompleted,
    totalModules,
    getWeeksForModule,
    isLoading: isLoadingPlan,
  } = useStudyPlan(user, modules);

  const { data: bookingsData, isLoading: isLoadingBookings } = useGetUserBookings(user?.id, undefined, {
    query: {
      enabled: !!user?.id,
      select: (data) => data?.data?.data || [],
    },
  });

  const upcomingBookings = useMemo(() => {
    if (!bookingsData) return [];
    return bookingsData
      .filter((b) => new Date(b.startTime) > new Date() && b.status !== 'cancelled')
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [bookingsData]);

  const courseGroups = useMemo(() => {
    if (!modules || modules.length === 0 || !studyPlan) return [];

    if (legacyMode) {
      // Fallback logic to consolidate multiple courses into one large list (Steinbeis cleanup fallback).
      // 1. Collect all unique courses from all modules
      const allCourses = new Map<string, { id: string; title: string }>();
      modules.forEach((m) => {
        m.courses?.forEach((c) => {
          if (!allCourses.has(c.id)) {
            allCourses.set(c.id, { id: c.id, title: c.title });
          }
        });
      });

      // 2. Filter out clearly deprecated/archived/individual courses
      const validCourses = Array.from(allCourses.values()).filter((c) => {
        const titleLower = c.title.toLowerCase();
        return (
          !titleLower.startsWith('z_archiv')
          && !titleLower.includes('deprecated')
          && !titleLower.includes('einzelkurs')
        );
      });

      // 3. Select the primary course: the one with the longest title (often the full program name)
      let primaryCourse = validCourses.length > 0
        ? validCourses.reduce((prev, current) => (prev.title.length > current.title.length ? prev : current))
        : Array.from(allCourses.values())[0]; // fallback to any course if all are filtered out

      if (!primaryCourse) {
        primaryCourse = { id: 'unknown', title: 'Study Program' };
      }

      // 4. Group ALL modules under this primary course to create one seamless study journey
      const groupedByCourse: Record<string, { id: string; title: string; modules: Module[] }> = {
        [primaryCourse.id]: {
          id: primaryCourse.id,
          title: primaryCourse.title,
          modules: [...modules],
        },
      };

      return Object.values(groupedByCourse).map((group): CourseGroup => {
        const semestersMap: Record<string, Semester> = {};
        let currentExpectedDate = new Date(studyPlan.enrollmentDate);

        group.modules.forEach((module: Module) => {
          const semester = getSemesterInfo(currentExpectedDate);

          if (!semestersMap[semester.id]) {
            semestersMap[semester.id] = {
              ...semester,
              modules: [],
            };
          }

          const weeks = getWeeksForModule(module.id);
          const finishDate = addWeeks(currentExpectedDate, weeks);

          semestersMap[semester.id].modules.push({
            ...module,
            expectedFinishDate: finishDate,
          });

          // Advance the date for next module
          currentExpectedDate = finishDate;
        });

        const semesters = Object.values(semestersMap).map((s) => {
          const startYear = parseInt(s.id.split('-')[1], 10);
          const startDate = new Date(startYear, s.startMonth, 1);
          const endDate = new Date(s.startMonth === 8 ? startYear + 1 : startYear, s.endMonth + 1, 0);

          return {
            ...s,
            year: startYear,
            dateRange: `${startDate.toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            })} - ${endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`,
          };
        });

        return {
          id: group.id,
          title: group.title,
          semesters: semesters.sort((a, b) => (a.year || 0) - (b.year || 0)),
        };
      });
    }

    // Default strict grouping behavior (when legacy compatibility is off)
    const groupedByCourse = modules.reduce<Record<string, { id: string; title: string; modules: Module[] }>>((acc, m) => {
      const courseId = m.courses?.[0]?.id || 'unknown';
      if (!acc[courseId]) {
        acc[courseId] = {
          id: courseId,
          title: m.courses?.[0]?.title || 'Unknown Course',
          modules: [],
        };
      }
      acc[courseId].modules.push(m);
      return acc;
    }, {});

    return Object.values(groupedByCourse).map((group): CourseGroup => {
      const semestersMap: Record<string, Semester> = {};
      let currentExpectedDate = new Date(studyPlan.enrollmentDate);

      group.modules.forEach((module: Module) => {
        const semester = getSemesterInfo(currentExpectedDate);

        if (!semestersMap[semester.id]) {
          semestersMap[semester.id] = {
            ...semester,
            modules: [],
          };
        }

        const weeks = getWeeksForModule(module.id);
        const finishDate = addWeeks(currentExpectedDate, weeks);

        semestersMap[semester.id].modules.push({
          ...module,
          expectedFinishDate: finishDate,
        });

        // Advance the date for next module
        currentExpectedDate = finishDate;
      });

      const semesters = Object.values(semestersMap).map((s) => {
        const startYear = parseInt(s.id.split('-')[1], 10);
        const startDate = new Date(startYear, s.startMonth, 1);
        const endDate = new Date(s.startMonth === 8 ? startYear + 1 : startYear, s.endMonth + 1, 0);

        return {
          ...s,
          year: startYear,
          dateRange: `${startDate.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
          })} - ${endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`,
        };
      });

      return {
        id: group.id,
        title: group.title,
        semesters: semesters.sort((a, b) => (a.year || 0) - (b.year || 0)),
      };
    });
  }, [modules, studyPlan, getWeeksForModule, legacyMode]);

  const activeModule = useMemo(() => {
    if (!modules || modules.length === 0) return null;
    if (selectedModule?.moduleId) {
      const found = modules.find((m) => m.id === selectedModule.moduleId);
      if (found) return found;
    }
    return modules.find((m) => m.uiState === 'active') || modules[0];
  }, [modules, selectedModule?.moduleId]);

  // @ts-expect-error - textSubtle is a semantic token
  const textSubtleValue = useToken('colors', 'textSubtle') as string;

  // Pre-calculate module color tokens to avoid calling useToken in map function
  // @ts-expect-error - module0500 is a dynamic token
  const module0 = useToken('colors', 'module0500') as string;
  // @ts-expect-error - module1500 is a dynamic token
  const module1 = useToken('colors', 'module1500') as string;
  // @ts-expect-error - module2500 is a dynamic token
  const module2 = useToken('colors', 'module2500') as string;
  // @ts-expect-error - module3500 is a dynamic token
  const module3 = useToken('colors', 'module3500') as string;
  // @ts-expect-error - module4500 is a dynamic token
  const module4 = useToken('colors', 'module4500') as string;
  // @ts-expect-error - module5500 is a dynamic token
  const module5 = useToken('colors', 'module5500') as string;
  // @ts-expect-error - module6500 is a dynamic token
  const module6 = useToken('colors', 'module6500') as string;
  // @ts-expect-error - module7500 is a dynamic token
  const module7 = useToken('colors', 'module7500') as string;
  // @ts-expect-error - module8500 is a dynamic token
  const module8 = useToken('colors', 'module8500') as string;
  // @ts-expect-error - module9500 is a dynamic token
  const module9 = useToken('colors', 'module9500') as string;

  const moduleColorTokens: Record<string, string> = {
    module0,
    module1,
    module2,
    module3,
    module4,
    module5,
    module6,
    module7,
    module8,
    module9,
  };

  if (isLoadingPlan) {
    return (
      <Center w="100%" h={displayHeight}>
        <Spinner />
      </Center>
    );
  }

  if (!modules || modules.length === 0 || courseGroups.length === 0) {
    return (
      <Center w="100%" h={displayHeight}>
        <Text>No program data found.</Text>
      </Center>
    );
  }

  return (
    <Box
      h={displayHeight}
      w="100%"
      testID="program-architect-container"
    >
      <ScrollView
        flex={1}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
      >
        <VStack
          space="xl"
          w="100%"
          maxWidth={1200}
          alignSelf="center"
          bg="$bgApp"
          px={media.md ? 60 : 20}
          pb={150}
          flexGrow={1}
        >
          <VStack space="xs" mt={media.md ? '$24' : '$20'}>
            <HStack space="sm" alignItems="center">
              <Box bg="$brandBase" p="$1.5" borderRadius="$md">
                <Icon as={GraduationCap} size="lg" color="$textOnBrand" />
              </Box>
              <Text
                size="xs"
                fontWeight="900"
                color="$textSubtle"
                textTransform="uppercase"
                letterSpacing={2}
              >
                Your Study Journey
              </Text>
            </HStack>
            <Heading size={media.md ? '3xl' : '2xl'} fontWeight="900" color="$textHeading">
              {courseGroups[0]?.title || 'Study Program'}
            </Heading>
            <HStack space="lg" mt="$1" alignItems="center" flexWrap="wrap">
              <HStack space="xs" alignItems="center">
                <Icon as={Calendar} size="xs" color={textSubtleValue} />
                <Text size="xs" color="$textSubtle">
                  Enrolled
                  {' '}
                  {studyPlan?.enrollmentDate ? new Date(studyPlan.enrollmentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown'}
                </Text>
              </HStack>
              <HStack space="xs" alignItems="center">
                <Icon as={Layout} size="xs" color={textSubtleValue} />
                <Text size="xs" color="$textSubtle">
                  {modules.length}
                  {' '}
                  Modules
                </Text>
              </HStack>

              {/* Optional: Add meaningful "On Track" metric here later if needed */}
            </HStack>

            {!legacyMode && (
              <StudyPacing
                studyPlan={studyPlan}
                updatePace={updatePace}
                projectedGraduationDate={projectedGraduationDate}
                progressPercentage={progressPercentage}
                modulesCompleted={modulesCompleted}
                totalModules={totalModules}
              />
            )}
          </VStack>

          <HStack space="lg" mt="$4" flexDirection={media.md ? 'row' : 'column'}>
            <VStack space="lg" flex={2}>
              {!!activeModule && activeModule.uiState === 'active' && (
                <Pressable
                  onPress={() => onSelectModule(activeModule.originalIndex || 0)}
                >
                  {({ pressed }) => (
                    <Box
                      borderRadius="$md"
                      overflow="hidden"
                      sx={{
                        ':hover': { opacity: 0.95 },
                        transform: [{ scale: pressed ? 0.995 : 1 }],
                      }}
                    >
                      <LinearGradient
                        colors={[primaryColorHex, '#2d3436']}
                        start={[0, 0]}
                        end={[1, 1]}
                      >
                        <VStack space="md" p={media.md ? '$8' : '$6'}>
                          <HStack justifyContent="space-between" alignItems="center">
                            <Box bg="rgba(255, 255, 255, 0.15)" px="$3" py="$1" borderRadius="$full">
                              <HStack space="xs" alignItems="center">
                                {activeModule.uiState === 'graded' ? (
                                  <Icon as={Trophy} size="2xs" color="$textOnBrand" />
                                ) : activeModule.uiState === 'submitted' ? (
                                  <Icon as={CheckCircle2} size="2xs" color="$textOnBrand" />
                                ) : (
                                  <Icon as={Clock} size="2xs" color="$textOnBrand" />
                                )}
                                <Text size="2xs" fontWeight="900" color="$textOnBrand" textTransform="uppercase">
                                  {activeModule.uiState === 'graded'
                                    ? 'Graded'
                                    : activeModule.uiState === 'submitted'
                                      ? 'Submitted'
                                      : 'Continue Learning'}
                                </Text>
                              </HStack>
                            </Box>
                          </HStack>
                          <VStack space="xs">
                            <Heading color="$textOnBrand" size="xl" lineHeight={32} paddingVertical={2}>
                              {activeModule.title}
                            </Heading>
                            <Text color="$textOnBrandMuted" size="sm" numberOfLines={2}>
                              {activeModule.metadata?.description?.replace(/<[^>]*>?/gm, '')}
                            </Text>
                          </VStack>
                          <HStack mt="$2">
                            {activeModule.uiState !== 'submitted' ? (
                              <Button
                                size="sm"
                                bg="$bgCard"
                                borderRadius="$full"
                                onPress={() => onOpenModule({
                                  moduleId: activeModule.id,
                                  courseId: activeModule.courses?.[0]?.id,
                                })}
                              >
                                <HStack space="xs" alignItems="center">
                                  {activeModule.uiState === 'graded' ? (
                                    <Icon as={Trophy} size="xs" color="$brandBase" />
                                  ) : (
                                    <Icon as={Play} size="xs" color="$brandBase" fill={primaryColorHex} />
                                  )}
                                  <ButtonText color="$brandBase" fontWeight={900}>
                                    {activeModule.uiState === 'graded' ? 'VIEW GRADE' : 'RESUME MODULE'}
                                  </ButtonText>
                                </HStack>
                              </Button>
                            ) : (
                              <HStack space="xs" alignItems="center">
                                <Icon as={Clock} size="sm" color="$textOnBrand" />
                                <Text size="sm" color="$textOnBrand" fontWeight="bold">
                                  {t('student.course.modulecard.grading-due-date')}
                                  :
                                  {' '}
                                  {activeModule.state?.gradingDue
                                    ? new Date(activeModule.state.gradingDue).toLocaleDateString()
                                    : 'TBD'}
                                </Text>
                              </HStack>
                            )}
                          </HStack>
                        </VStack>
                      </LinearGradient>
                    </Box>
                  )}
                </Pressable>
              )}
            </VStack>

            <VStack space="lg" flex={1}>
              <VStack space="md">
                <HStack space="sm" alignItems="center">
                  <Icon as={Bell} size="md" color="$brandBase" />
                  <Heading size="sm" fontWeight="900">
                    Upcoming Activity
                  </Heading>
                </HStack>

                <VStack space="md">
                  {isLoadingBookings ? (
                    <Spinner color="$brandBase" />
                  ) : upcomingBookings.length > 0 ? (
                    upcomingBookings.map((booking) => (
                      <ActivityCard
                        key={booking.bookingId}
                        testID={`activity-card-${booking.bookingId}`}
                        booking={booking}
                        modules={modules}
                        onOpenModule={onOpenModule}
                      />
                    ))
                  ) : (
                    <Box
                      bg="$bgCard"
                      p="$6"
                      borderRadius="$md"
                      borderWidth={1}
                      borderColor="$borderLight"
                    >
                      <HStack space="md" alignItems="center">
                        <Box bg="$bgSubtle" p="$3" borderRadius="$full">
                          <Icon as={Calendar} size="lg" color={textSubtleValue} />
                        </Box>
                        <VStack flex={1}>
                          <Text size="sm" fontWeight="bold">
                            No upcoming sessions
                          </Text>
                          <Text size="xs" color="$textSubtle">
                            Book your next session in the modules.
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </VStack>
            </VStack>
          </HStack>

          <VStack space="xl" mt="$8" pb="$20">
            {courseGroups.map((course) => (
              <VStack key={`course-${course.id}`} space="lg">
                <HStack space="md" alignItems="center">
                  <Box w={4} h={24} bg="$brandBase" borderRadius="$full" />
                  <Heading size="md" fontWeight="900" color="$textHeading">
                    {course.title}
                  </Heading>
                </HStack>

                <VStack space="lg">
                  {course.semesters.map((semester) => (
                    <VStack key={`semester-${course.id}-${semester.id}`} space="md">
                      {!legacyMode && (
                        <HStack justifyContent="space-between" alignItems="center">
                          <VStack>
                            <Text size="sm" fontWeight="900" color="$textHeading">
                              {semester.title}
                            </Text>
                            <Text size="xs" color="$textSubtle">
                              {semester.dateRange}
                            </Text>
                          </VStack>
                          {semester.modules.some((m) => m.id === activeModule?.id) && (
                            <Box bg="$bgOverlay" px="$2" py="$0.5" borderRadius="$md">
                              <Text size="2xs" fontWeight="900" color="$textHeading">
                                CURRENT
                              </Text>
                            </Box>
                          )}
                        </HStack>
                      )}

                      <VStack space="md" maxWidth={800}>
                        {semester.modules.map((module) => {
                          const isCurrentFocus = activeModule?.id === module.id;
                          const state = module.uiState || 'not-started';
                          const isGraded = state === 'graded';
                          const isSubmitted = state === 'submitted';
                          const isLocked = state === 'locked';
                          const isActive = state === 'active';

                          const colorName = getColorForModule(module);
                          const moduleColor = moduleColorTokens[colorName];

                          return (
                            <Pressable
                              key={`module-${module.id}-${course.id}`}
                              onPress={() => onSelectModule(module.originalIndex || 0)}
                            >
                              {({ pressed }) => (
                                <Box
                                  bg={isLocked ? '$bgSubtle' : '$bgCard'}
                                  p="$4"
                                  borderRadius="$md"
                                  borderWidth={1}
                                  borderColor={isCurrentFocus ? '$borderBrand' : '$borderLight'}
                                  overflow="hidden"
                                  sx={{
                                    ':hover': { bg: isLocked ? '$bgSubtle' : '$bgHover' },
                                    transform: [{ scale: pressed ? 0.995 : 1 }],
                                  }}
                                >
                                  <Box
                                    position="absolute"
                                    left={0}
                                    top={0}
                                    bottom={0}
                                    w={4}
                                    bg={isLocked ? '$neutral300' : moduleColor}
                                  />

                                  <HStack space="md" alignItems="center" pl="$2">
                                    <VStack flex={1} space="xs">
                                      {/* Status Row */}
                                      <HStack space="sm" alignItems="center" h={18}>
                                        {isGraded ? (
                                          <>
                                            <Text
                                              size="2xs"
                                              fontWeight="bold"
                                              color="$statusSuccess"
                                              textTransform="uppercase"
                                              letterSpacing="$md"
                                            >
                                              Graded
                                            </Text>
                                            {module.grading?.grade !== undefined && module.grading?.grade !== null && (
                                              <Box bg="$statusSuccessSubtle" px="$1.5" py="$0.5" borderRadius="$sm">
                                                <Text size="2xs" fontWeight="bold" color="$statusSuccess">
                                                  {module.grading.grade.toFixed(1)}
                                                </Text>
                                              </Box>
                                            )}
                                          </>
                                        ) : isSubmitted ? (
                                          <Text
                                            size="2xs"
                                            fontWeight="bold"
                                            color="$statusInfo"
                                            textTransform="uppercase"
                                            letterSpacing="$md"
                                          >
                                            Submitted
                                          </Text>
                                        ) : isActive || isCurrentFocus ? (
                                          <Text
                                            size="2xs"
                                            fontWeight="bold"
                                            color="$brandBase"
                                            textTransform="uppercase"
                                            letterSpacing="$md"
                                          >
                                            Active
                                          </Text>
                                        ) : null}
                                      </HStack>

                                      <Text
                                        size="md"
                                        fontWeight="bold"
                                        color={isLocked ? '$textDisabled' : '$textHeading'}
                                        numberOfLines={1}
                                      >
                                        {module.title}
                                      </Text>

                                      <HStack space="xs" alignItems="center">
                                        <Text size="xs" color="$textSubtle">
                                          {module.metadata?.ects || 0}
                                          {' '}
                                          ECTS
                                        </Text>
                                        <Box w={3} h={3} borderRadius="$full" bg="$neutral300" />
                                        <Text size="xs" color="$textSubtle">
                                          {module.metadata?.learningHours || 0}
                                          {' '}
                                          Hours
                                        </Text>
                                        {module.expectedFinishDate && (
                                        <>
                                          <Box w={3} h={3} borderRadius="$full" bg="$neutral300" />
                                          <Text size="xs" color="$textSubtle">
                                            ~
                                            {' '}
                                            {module.expectedFinishDate.toLocaleDateString(undefined, { month: 'short' })}
                                          </Text>
                                        </>
                                        )}
                                      </HStack>
                                    </VStack>

                                    {/* Icon Column */}
                                    <Box w={32} alignItems="center">
                                      {isGraded ? (
                                        <Icon as={Trophy} size="sm" color="$statusSuccess" />
                                      ) : isSubmitted ? (
                                        <Icon as={GraduationCap} size="lg" color="$statusInfo" />
                                      ) : isLocked ? (
                                        <Icon as={Lock} size="sm" color="$textDisabled" />
                                      ) : (
                                        <Icon
                                          as={Circle}
                                          size="sm"
                                          color={isCurrentFocus || isActive ? '$brandBase' : '$borderLight'}
                                          // @ts-expect-error - strokeWidth is supported by lucide icons
                                          // but not explicitly in Gluestack props
                                          strokeWidth={3}
                                        />
                                      )}
                                    </Box>
                                  </HStack>
                                </Box>
                              )}
                            </Pressable>
                          );
                        })}
                      </VStack>
                    </VStack>
                  ))}
                </VStack>
              </VStack>
            ))}
          </VStack>
        </VStack>
      </ScrollView>
    </Box>
  );
};

export default ProgramArchitect;
